import { useMutation } from '@tanstack/react-query';

import Axios from 'axios';

import { forceReloadContactExternalAccess } from '@hooks/API/Contact/UseContactExternalAccess';
import { forceReloadModelDataUpdates } from '@hooks/API/Contact/UseModelDataUpdates';

const sendModelDataUpdateRequest = ({ contactId, signatureId, message }) =>
    Axios.post('/ContactGateway/SendUpdateDataRequest', {
        ContactIDs: [contactId],
        SignatureID: signatureId,
        Message: message,
    });

const useSendModelDataUpdateRequest = () => {
    return useMutation({
        mutationFn: sendModelDataUpdateRequest,
        onSuccess: (_data, variables) => {
            forceReloadModelDataUpdates();
            forceReloadContactExternalAccess(variables.contactId);
        },
    });
};

const sendMassiveModelDataUpdateRequest = ({ contacts, signatureId, message }) =>
    Axios.post('/ContactGateway/SendMassiveUpdateDataRequest', {
        Contacts: [...contacts],
        SignatureID: signatureId,
        Message: message,
    });

const useSendMassiveModelDataUpdateRequest = () => {
    return useMutation({
        mutationFn: sendMassiveModelDataUpdateRequest,
        onSuccess: (_data, variables) => {
            forceReloadModelDataUpdates();
            (variables.contacts || []).forEach((contact) => {
                if (contact?.ID != null) {
                    forceReloadContactExternalAccess(contact.ID);
                }
            });
        },
    });
};

// Reminders do not change the access or update-request state of the model,
// so we intentionally skip cache invalidation here.
const sendModelDataUpdateReminder = ({ contactIds, signatureId, message }) =>
    Axios.post('/ContactGateway/SendReminder', {
        ContactIDs: [...contactIds],
        SignatureID: signatureId,
        Message: message,
    });

const useSendModelDataUpdateReminder = () => {
    return useMutation({
        mutationFn: sendModelDataUpdateReminder,
    });
};

const allowContactAccess = ({ contactId, email, enableUpdates = true }) =>
    Axios.put('/ContactGateway/AllowAccess', {
        ContactID: contactId,
        Email: email,
        EnableUpdates: enableUpdates,
    });

const useAllowContactAccess = () => {
    return useMutation({
        mutationFn: allowContactAccess,
        onSuccess: (_data, variables) => {
            forceReloadContactExternalAccess(variables.contactId);
        },
    });
};

const updateContactAccess = ({ contactId, email, enableUpdates = true }) =>
    Axios.put('/ContactGateway/UpdateAccess', {
        ContactID: contactId,
        Email: email,
        EnableUpdates: enableUpdates,
    });

const useUpdateContactAccess = () => {
    return useMutation({
        mutationFn: updateContactAccess,
        onSuccess: (_data, variables) => {
            forceReloadContactExternalAccess(variables.contactId);
        },
    });
};

const revokeContactAccess = ({ contactId }) => Axios.put(`/ContactGateway/RevokeAccess/${contactId}`);

const useRevokeContactAccess = () => {
    return useMutation({
        mutationFn: revokeContactAccess,
        onSuccess: (_data, variables) => {
            forceReloadContactExternalAccess(variables.contactId);
        },
    });
};

export {
    useSendModelDataUpdateRequest,
    useSendMassiveModelDataUpdateRequest,
    useSendModelDataUpdateReminder,
    useAllowContactAccess,
    useUpdateContactAccess,
    useRevokeContactAccess,
};
